/**
 * The plan lane, client side (docs/plans.md §14.3).
 *
 * TWO RULES SHAPE EVERY PARSER HERE.
 *
 * **The disclosure is never composed on this side.** The server authors the
 * sentence; this module carries it, the screen renders it, and `hashConsent`
 * returns the SHA-256 of exactly what was rendered. The server re-derives its
 * own text and refuses on a mismatch, so a client that edited the words gets a
 * 409 rather than a subscription its buyer never agreed to.
 *
 * **Usage arrives as a SHARE, and there is nowhere here to put a capacity.**
 * `PlanState` has `used_share` and no `included_tasks` field to fill, which is
 * the structural version of the rule rather than a promise about it. Ruling C2
 * extends that to CHECKOUT: the ladder and the offers carry PRICES and RATIOS
 * and no quantity of any kind, because a price is not a capacity and the
 * auto-renewal rules require one while forbidding the other.
 *
 * With plans dark the server answers 404, and `PLANS_DISABLED` is what this
 * returns — the screen renders nothing rather than an error, because an address
 * with no feature behind it is not a fault.
 */
import type { CheckoutOpening } from "../checkout";
import {
  PLAN_TIERS as SHARED_PLAN_TIERS,
  type PlanCheckoutSurface,
  type PlanTierName,
} from "@nob/schemas";
import { API_URL, call, get, post, num, readOpening, type RawError } from "./core";

/** The tier names come from `@nob/schemas` — one list for the whole product,
 * after a second copy of it drifted and made the server refuse its own offer
 * (2026-09-08). */
export type PlanTier = PlanTierName;
export type PlanInterval = "monthly" | "annual";
export type PlanLifecycle = "active" | "trialing" | "past_due" | "canceled" | "lapsed";

export const PLAN_TIERS: readonly PlanTier[] = SHARED_PLAN_TIERS;
const INTERVALS: PlanInterval[] = ["monthly", "annual"];

/** The tier names, in a person's words. Ours, not the server's — same rule as packs. */
export const TIER_NAMES: Record<PlanTier, string> = {
  free: "Free",
  lite: "Lite",
  personal: "Personal",
  pro5: "Pro 5×",
  pro20: "Pro 20×",
};

export type PlanState = {
  tier: PlanTier;
  state: PlanLifecycle;
  interval: PlanInterval | null;
  /** 0 through 1. There is no capacity on this object, by design. */
  /**
   * Whether this tier meters AI work at all. FALSE ON FREE (ruling C10), which
   * is a different sentence from "metered and used up" — a Free door performs no
   * AI work, so it has no allowance to have spent. Every surface that renders a
   * ring or a share has to branch on this before it branches on `overflow`.
   */
  metered: boolean;
  /**
   * A SHARE OF THE WINDOW'S ALLOWANCE — 1 at the allowance, and past 1 beyond
   * it (ruling R-0923-3 (8)): pay as you go, or the one piece of work that
   * crossed the line. Never a capacity, and never a share of the pay-as-you-go
   * line, so 1.35 means the same on every plan.
   */
  used_share: number;
  overflow: boolean;
  /** When the open allowance window began (ruling R-0923-2). Null on Free. */
  window_start: string | null;
  /**
   * When the allowance turns over: the OPEN WINDOW's end — a month from its
   * start on every paid plan, annual included, and the trial's end on a trial.
   * Null on Free. "Resets on" reads this.
   */
  resets_on: string | null;
  /**
   * The BILLING period's end — the renewal, or the day a requested cancellation
   * lands. On an annual plan it is not `resets_on`: "Ending on" reads this.
   */
  period_end: string | null;
  trial_end: string | null;
  cancel_requested_at: string | null;
  scheduled_tier: PlanTier | null;
  scheduled_at: string | null;
  /** An accepted upgrade the processor's webhook has not applied yet (wave 124). */
  pending_tier: PlanTier | null;
  pending_interval: PlanInterval | null;
  next_tier: PlanTier | null;
  /** A dispute is open and paid features are suspended (§8.9). One quiet line. */
  paid_features_suspended: boolean;
  /** The UK/EEA 14-day window while it is open (§8.6), or null. */
  cooling_off: { until: string; unused: boolean } | null;
  /**
   * **THE USAGE NOTICE** (ruling R-0921-2 (9)) — `approaching` from 80% of the
   * period, `used` once what is left cannot bear a hearing, null otherwise.
   *
   * THE SENTENCE COMES WITH IT, server-authored, and the client renders what it
   * was handed. A copy kept here would be a second copy of a statement about
   * what somebody has bought, drifting until the day the two disagreed — the
   * same discipline every consent string in this product keeps. Neither the
   * word nor the line carries a figure of any kind (ruling C2).
   */
  usage_notice: "approaching" | "used" | null;
  usage_notice_line: string | null;
  /**
   * "Get more usage", or "Start a plan" where §8.3 offers no reset. On every
   * read, notice or not: it is also the one control a conversation that met
   * the wall draws (wave 57). Null only from a server that did not send it.
   */
  usage_notice_action: string | null;
};

export type LadderRung = {
  tier: PlanTier;
  /** The tier's name, the server's own (`Free`, `Lite`, `Personal`, `Pro 5×`, `Pro 20×`). */
  label: string;
  /**
   * NO QUANTITY (ruling C2). A rung is described by its RATIO to another rung —
   * "three times Lite" — and the server sends that ratio rather than a count,
   * because a published quantity is a promise we would have to break the first
   * time a model reprices. `plans/capacity.test.ts` greps this bundle for one.
   */
  relative_to: { tier: PlanTier; times: number } | null;
  monthly_display: string;
  /**
   * "$6" — the PER-MONTH figure of the annual plan, computed on the server
   * (economics-wire §2.1). Null where the rung is not sold annually. The client
   * never divides by twelve: the two figures on a card have to be
   * arithmetically consistent or the card is a misrepresentation.
   */
  annual_per_month_display: string | null;
  /** What the annual charge actually is, in cents — for the "billed $72 a year" half. */
  annual_cents: number | null;
  /**
   * Month-to-month only: the rung is NOT SOLD annually at all, so its annual
   * price is null rather than twelve times the monthly one. No annual toggle
   * for it, and the screen says why (§4).
   */
  no_commitment: boolean;
  /** False on Free: this rung performs no AI work (ruling C10). */
  metered: boolean;
};

export type PlanOffer = {
  tier: PlanTier;
  interval: PlanInterval;
  price_display: string;
  first_charge_on: string;
  /** THE server-authored sentence. Rendered verbatim; hashed as rendered. */
  disclosure: string;
  is_downgrade: boolean;
};

/**
 * The trial, or nothing.
 *
 * **THERE IS NO `tasks` FIELD AND THERE WILL NOT BE ONE** (economics-wire §2.3).
 * The trial grants the FULL limit of its tier for its own length — the counter
 * runs exactly as a paid period's does, and the only difference is the header on
 * the bar. Version 1.0 pro-rated the allowance and stated the resulting number;
 * ruling C2 takes both halves away.
 */
export type TrialOffer = {
  tier: PlanTier;
  days: number;
  /**
   * Which arm of FR-5.8 this is, and the ONLY thing `x_is_premium_plus` ever
   * says to a client (FR-5.8 #7). The money step's headline reads "X Premium on
   * this account." or "No X Premium on this one.", and that is the server's
   * answer rather than a tier name this side reverse-engineers — the tiers move
   * and the sentence does not.
   */
  premium: boolean;
  options: Array<{
    interval: PlanInterval;
    price_display: string;
    /**
     * "$20/month" — what the plan costs per month once the trial ends, unit
     * attached, composed on the server (owner, 2026-09-07). The trial sheet
     * leads with it: "Pay $0 now. $20/month after the trial ends."
     *
     * An ANNUAL option carries its price per month, because that is the figure
     * a buyer compares; the annual total is in the disclosure underneath. This
     * side neither divides nor formats it — a price is a stated number under a
     * notice obligation and it is rendered exactly as it arrived.
     */
    after_trial_monthly_display: string;
    first_charge_on: string;
    disclosure: string;
  }>;
};

export type PlanView = {
  plan: PlanState;
  ladder: LadderRung[];
  offers: PlanOffer[];
  trial: TrialOffer | null;
  /**
   * THE UK/EEA WITHDRAWAL-RIGHT CONTROL, OR NULL (ToU 2.1 §8.6, ruling
   * R-0914-1 (3)).
   *
   * The SERVER decides whether this applies, from the jurisdiction recorded on
   * the account, and hands down the exact sentence it will check the hash of.
   * The client renders a second control when it is a string and nothing at all
   * when it is null — it never works the question out for itself, because a
   * client that could decide the control applied could decide it away.
   */
  withdrawal_waiver: string | null;
  /**
   * THE DRAWDOWN DISCLOSURE (Terms 2.11 §5.3, §8.7), the server's one fixed
   * sentence: rendered beside the subscription's or the trial's on every
   * checkout screen, and hashed back with the purchase. Null only when the
   * server sent none, and then no purchase can start from this side — it is
   * not a sentence this side can supply.
   */
  drawdown: string | null;
  /**
   * THE NOTICES THE OWNER HAS PUBLISHED TO THIS PERSON (ruling R-0923-1 (5)),
   * newest first — a boost explained, a complimentary reset announced, or a
   * line on its own. The SERVER decides who sees which and until when; this
   * side draws the list it was handed, text as sent, and composes nothing.
   */
  notices: PlanNotice[];
  /**
   * PAY AS YOU GO, AS BILLING SHOWS IT (ruling R-0923-3), or null — on Free
   * and on a trial, which see nothing of it. Every sentence on it is the
   * server's; the dollars on it are the subscriber's own and belong to billing
   * alone, never to a conversation, the composer or a door page.
   */
  payg: PaygView | null;
  /**
   * WHAT THE PRICES ON THIS SCREEN INCLUDE (ruling R-0923-5). Every figure
   * above arrives already including VAT or GST where the account's country
   * expects it; this says so. `suffix` is the label a price carries on a
   * screen ("incl. VAT", or "plus tax" where the rate would not come) and null
   * where prices read as they always have; `line` is the one sentence about
   * tax, or null where no tax is charged.
   */
  tax: TaxView;
  /**
   * AN ACCOUNT CLOSURE WAITING ON PAYMENT (ruling R-0923-5 (3)), or null.
   * The server's sentence, and the bills it waits on with where to pay each.
   */
  closure: ClosureView | null;
  /**
   * THE MANAGE GROUP'S FACTS (wave 87), or null where the read failed: the
   * card on file, the billing country, the newest invoice's date, and a
   * payment that needs the subscriber. Which rows show is decided from these,
   * and the notice's sentence is the server's.
   */
  billing: BillingManage | null;
};

/** Whether a card has expired or is about to — the server's reading, on its clock. */
export type CardExpiry = "ok" | "soon" | "expired";

export type BillingManage = {
  /** A processor customer exists: a trial or a paid plan, now or before. */
  customer: boolean;
  card: {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
    expiry: CardExpiry;
  } | null;
  /** ISO 3166 alpha-2. */
  country: string | null;
  latest_invoice_at: string | null;
  notice: { kind: "action_required" | "failed"; line: string; invoice_id: string | null } | null;
};

export type InvoiceStatus = "paid" | "due" | "failed" | "refunded" | "partly_refunded";

/** One invoice or receipt, as the list shows it. Dollars are allowed: this is billing. */
export type InvoiceRow = {
  id: string;
  date: string;
  description: string;
  total_cents: number;
  total_display: string;
  status: InvoiceStatus;
};

export type InvoicesPage = { invoices: InvoiceRow[]; next_cursor: string | null };

export type TaxView = {
  inclusive: boolean;
  label: "VAT" | "GST" | null;
  suffix: string | null;
  line: string | null;
};

export type ClosureView = {
  state: "billing" | "waiting_payment" | "ready";
  line: string;
  bills: PaygBill[];
};

/** One notice: its words, and when it stops showing. No figure is ever in it. */
export type PlanNotice = { id: string; body: string; until: string };

/** One pay-as-you-go bill, as billing lists it. */
export type PaygBill = {
  id: string;
  kind: "early" | "remainder" | "quarterly" | "cancellation";
  amount_display: string;
  status: "pending" | "paid" | "failed";
  /** It rode the plan's renewal invoice rather than being a bill of its own. */
  on_renewal: boolean;
  created_at: string;
  window_start: string;
  window_end: string;
  /** The processor's page to confirm a charge the bank sent back, or null. */
  action_url: string | null;
  /** The processor's page to pay a failed charge on, or null (R-0923-5 (3)). */
  pay_url: string | null;
};

export type PaygView = {
  on: boolean;
  max_cents: number | null;
  /** "$50 a month incl. VAT" — the maximum with its unit and, where it has one, its label. */
  max_display: string | null;
  price_display: string;
  /**
   * What the maximum entered here includes — "incl. VAT" / "incl. GST" — or
   * null where it is before tax as the plan's price is (ruling R-0923-5 (2)).
   */
  amounts_suffix: string | null;
  min_max_cents: number;
  max_max_cents: number;
  default_max_cents: number;
  rate_line: string;
  accrued_display: string;
  accrued_cents: number;
  past_allowance: boolean;
  at_maximum: boolean;
  available: boolean;
  unavailable_reason: string | null;
  /** The opt-in sentence for the prefilled (or current) maximum — the server's. */
  consent_text: string;
  /** The turning-off sentence, when on. */
  off_text: string | null;
  /** The drawdown consent, shown and hashed beside the opt-in. */
  drawdown: string;
  bills: PaygBill[];
  notices: string[];
};

/**
 * EVERY REFUSAL IS A DIFFERENT SENTENCE (ruling R-0908-1), so every refusal the
 * server can make has a code here. Three of these are new, and each was
 * previously indistinguishable from something it has nothing to do with:
 *
 *  - `NO_SUBSCRIPTION` — there is no plan on this account. It was not mapped at
 *    all and fell in with `PLANS_DISABLED` on the shared 404.
 *  - `SAME_RUNG` — you are already on that plan. It was mapped to
 *    `NOT_SUBSCRIBED`, which told the owner a plan they are paying for is not
 *    theirs to change.
 *  - `PROCESSOR_REFUSED` — the payment side refused or did not answer. It used
 *    to arrive as a 500 and read as "Nob is not answering", which is also what
 *    an expired session and a dark feature flag said.
 */
export type PlanFailureCode =
  | "UNAUTHENTICATED"
  | "PLANS_DISABLED"
  | "CONSENT_MISMATCH"
  | "NOT_SUBSCRIBED"
  | "NO_SUBSCRIPTION"
  | "SAME_RUNG"
  | "PROCESSOR_REFUSED"
  | "VALIDATION_ERROR"
  /**
   * A trial's question, asked where there is no trial, or left unanswered
   * (ruling R-0909-1). Two codes because they are two different sentences: one
   * says the screen is out of date, the other says the screen skipped a step —
   * and neither is "Nob is not answering".
   */
  | "NOT_TRIALING"
  | "CHOICE_REQUIRED"
  /** The plan's payment is failing: the card first, then any change (ruling R-1006-3). */
  | "PAYMENT_PAST_DUE"
  | "UNKNOWN";

export type PlanResult<T> = ({ ok: true } & T) | { ok: false; code: PlanFailureCode };

const PLAN_OFFLINE: { ok: false; code: "UNKNOWN" } = { ok: false, code: "UNKNOWN" };

/**
 * THE SERVER'S CODE FIRST, THE STATUS ONLY AS A FALLBACK. The body says what
 * happened; a status is a category several different things share, and reading
 * it first is how `NO_SUBSCRIPTION` spent its life being reported as "plans are
 * not on here" (ruling R-0908-1).
 */
function planFailure(status: number, body: RawError): { ok: false; code: PlanFailureCode } {
  const code = body.error?.code;
  if (status === 401) return { ok: false, code: "UNAUTHENTICATED" };
  if (code === "CONSENT_MISMATCH") return { ok: false, code: "CONSENT_MISMATCH" };
  if (code === "NO_SUBSCRIPTION") return { ok: false, code: "NO_SUBSCRIPTION" };
  if (code === "SAME_RUNG") return { ok: false, code: "SAME_RUNG" };
  if (code === "NOT_TRIALING") return { ok: false, code: "NOT_TRIALING" };
  if (code === "CHOICE_REQUIRED") return { ok: false, code: "CHOICE_REQUIRED" };
  if (code === "PAYMENT_PAST_DUE") return { ok: false, code: "PAYMENT_PAST_DUE" };
  if (code === "NOT_SUBSCRIBED") return { ok: false, code: "NOT_SUBSCRIBED" };
  if (code === "PROCESSOR_REFUSED" || status === 502) {
    return { ok: false, code: "PROCESSOR_REFUSED" };
  }
  if (code === "PLANS_DISABLED" || status === 404) return { ok: false, code: "PLANS_DISABLED" };
  if (code === "VALIDATION_ERROR" || status === 400) return { ok: false, code: "VALIDATION_ERROR" };
  return PLAN_OFFLINE;
}

function tierOf(raw: unknown, fallback: PlanTier | null): PlanTier | null {
  return PLAN_TIERS.find((known) => known === raw) ?? fallback;
}

function intervalOf(raw: unknown): PlanInterval | null {
  return INTERVALS.find((known) => known === raw) ?? null;
}

function textOrNull(raw: unknown): string | null {
  return typeof raw === "string" && raw.length > 0 ? raw : null;
}

function normalisePlan(raw: unknown): PlanState {
  const p = (raw ?? {}) as Record<string, unknown>;
  const states: PlanLifecycle[] = ["active", "trialing", "past_due", "canceled", "lapsed"];
  return {
    tier: tierOf(p["tier"], "free") ?? "free",
    state: states.find((known) => known === p["state"]) ?? "active",
    interval: intervalOf(p["interval"]),
    // Defaults TRUE, so a payload from a server that predates the field is
    // read as a metered plan rather than silently rendering every tier
    // as Free.
    metered: p["metered"] !== false,
    // Floored at nought and NOT capped at one: past the allowance is a real
    // place to be now (ruling R-0923-3 (8)), and a clamp here would draw 135%
    // as a plan with exactly nothing left.
    used_share: Math.max(num(p["used_share"]), 0),
    overflow: p["overflow"] === true,
    window_start: textOrNull(p["window_start"]),
    resets_on: textOrNull(p["resets_on"]),
    period_end: textOrNull(p["period_end"]),
    trial_end: textOrNull(p["trial_end"]),
    cancel_requested_at: textOrNull(p["cancel_requested_at"]),
    scheduled_tier: tierOf(p["scheduled_tier"], null),
    scheduled_at: textOrNull(p["scheduled_at"]),
    pending_tier: tierOf(p["pending_tier"], null),
    pending_interval: intervalOf(p["pending_interval"]),
    next_tier: tierOf(p["next_tier"], null),
    paid_features_suspended: p["paid_features_suspended"] === true,
    cooling_off: normaliseCoolingOff(p["cooling_off"]),
    // Strictly one of the two words, or none: an unknown value is not a notice
    // the screen knows how to draw, and drawing nothing is the safe error.
    usage_notice:
      p["usage_notice"] === "approaching" || p["usage_notice"] === "used"
        ? p["usage_notice"]
        : null,
    usage_notice_line: textOrNull(p["usage_notice_line"]),
    usage_notice_action: textOrNull(p["usage_notice_action"]),
  };
}

/**
 * THE ROADS OUT OF A 402 (`billing/exhausted.ts#hearingRoads`, ToU 2.2 §5.3),
 * AS EVERY CONVERSATION CARRIES THEM.
 *
 * Every metered lane meets the same refusal — a knock, a hearing's pause, a
 * held result, a doorman turn, the owner's chat on a knock, a public-line
 * draft — and none of them carries a money figure. A conversation that met the
 * wall shows one control, and the plan sheet it opens reads its options from
 * the plan route (wave 57). The paid reset that was a road here is deleted
 * (ruling R-0923-3 (1)).
 */
export type HearingRoads = {
  next_tier: PlanTier | null;
  /** True on Free and on a trial, where the road is starting a plan (§8.3). */
  start_plan: boolean;
  resets_on: string | null;
};

export function normaliseHearingRoads(raw: unknown): HearingRoads {
  const d = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  return {
    next_tier: tierOf(d["next_tier"], null),
    start_plan: d["start_plan"] === true,
    resets_on: textOrNull(d["resets_on"]),
  };
}

/**
 * WHETHER THE ONE ROAD IS "START THE PLAN" (ToU 2.2 §8.3).
 *
 * The server derives exactly this into a 402's `start_plan`, and a screen that
 * met a 402 uses the server's answer. This exists for the screens that did NOT
 * meet one — Settings → Plan, and a hearing whose stream refused with a bare
 * code — where there is no refusal body to read it out of. It is a statement
 * about a tier and a lifecycle, both of which the plan payload carries, and it
 * decides nothing a client is not allowed to decide.
 */
export function startPlanRoad(plan: PlanState): boolean {
  return plan.tier === "free" || plan.state === "trialing";
}

function normaliseCoolingOff(raw: unknown): { until: string; unused: boolean } | null {
  if (raw === null || typeof raw !== "object") return null;
  const c = raw as Record<string, unknown>;
  const until = textOrNull(c["until"]);
  return until ? { until, unused: c["unused"] === true } : null;
}

/** The ratio a rung publishes, or null. Never a count (ruling C2). */
function relativeTo(raw: unknown): { tier: PlanTier; times: number } | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const tier = tierOf(r["tier"], null);
  const times = num(r["times"]);
  return tier && times > 0 ? { tier, times } : null;
}

function normaliseLadder(raw: unknown): LadderRung[] {
  if (!Array.isArray(raw)) return [];
  const rungs: LadderRung[] = [];
  for (const item of raw) {
    const r = (item ?? {}) as Record<string, unknown>;
    const tier = tierOf(r["tier"], null);
    if (!tier) continue;
    rungs.push({
      tier,
      // The server names its own tiers (economics-wire §9); a rung that arrives
      // unnamed falls back to the table above rather than rendering blank.
      label: typeof r["label"] === "string" && r["label"] ? r["label"] : TIER_NAMES[tier],
      relative_to: relativeTo(r["relative_to"]),
      monthly_display: typeof r["monthly_display"] === "string" ? r["monthly_display"] : "",
      annual_per_month_display: textOrNull(r["annual_per_month_display"]),
      annual_cents: typeof r["annual_cents"] === "number" ? r["annual_cents"] : null,
      no_commitment: r["no_commitment"] === true,
      // Defaults TRUE for the same reason `PlanState.metered` does: a payload
      // from a server that predates the field reads as metered rather than
      // rendering every rung as Free.
      metered: r["metered"] !== false,
    });
  }
  return rungs;
}

function normaliseOffers(raw: unknown): PlanOffer[] {
  if (!Array.isArray(raw)) return [];
  const offers: PlanOffer[] = [];
  for (const item of raw) {
    const o = (item ?? {}) as Record<string, unknown>;
    const tier = tierOf(o["tier"], null);
    const interval = intervalOf(o["interval"]);
    const disclosure = typeof o["disclosure"] === "string" ? o["disclosure"] : "";
    const firstChargeOn = textOrNull(o["first_charge_on"]);
    // An offer with no sentence behind it is one nobody could consent to, so it
    // is dropped rather than rendered with a blank where the terms go.
    if (!tier || tier === "free" || !interval || !disclosure || !firstChargeOn) continue;
    offers.push({
      tier,
      interval,
      price_display: typeof o["price_display"] === "string" ? o["price_display"] : "",
      first_charge_on: firstChargeOn,
      disclosure,
      is_downgrade: o["is_downgrade"] === true,
    });
  }
  return offers;
}

function normaliseTrial(raw: unknown): TrialOffer | null {
  if (!raw || typeof raw !== "object") return null;
  const t = raw as Record<string, unknown>;
  const tier = tierOf(t["tier"], null);
  if (!tier || tier === "free") return null;
  const options: TrialOffer["options"] = [];
  if (Array.isArray(t["options"])) {
    for (const item of t["options"]) {
      const o = (item ?? {}) as Record<string, unknown>;
      const interval = intervalOf(o["interval"]);
      const disclosure = typeof o["disclosure"] === "string" ? o["disclosure"] : "";
      const firstChargeOn = textOrNull(o["first_charge_on"]);
      // The price after the trial is as load-bearing as the disclosure now that
      // the sheet leads with it, so an option that arrives without one is
      // dropped rather than rendered as "Pay $0 now.  after the trial ends."
      const afterTrial = textOrNull(o["after_trial_monthly_display"]);
      if (!interval || !disclosure || !firstChargeOn || !afterTrial) continue;
      options.push({
        interval,
        price_display: typeof o["price_display"] === "string" ? o["price_display"] : "",
        after_trial_monthly_display: afterTrial,
        first_charge_on: firstChargeOn,
        disclosure,
      });
    }
  }
  if (options.length === 0) return null;
  return { tier, days: num(t["days"]), premium: t["premium"] === true, options };
}

export async function fetchPlan(): Promise<PlanResult<{ view: PlanView }>> {
  const r = await call(() => get("/api/v1/plan"));
  if (!r) return PLAN_OFFLINE;
  if (!r.res.ok) return planFailure(r.res.status, r.body as RawError);
  const b = r.body as Record<string, unknown>;
  return {
    ok: true,
    view: {
      plan: normalisePlan(b["plan"]),
      ladder: normaliseLadder(b["ladder"]),
      offers: normaliseOffers(b["offers"]),
      trial: normaliseTrial(b["trial"]),
      withdrawal_waiver: textOrNull(b["withdrawal_waiver"]),
      drawdown: textOrNull(b["drawdown"]),
      notices: normaliseNotices(b["notices"]),
      payg: normalisePayg(b["payg"]),
      tax: normaliseTax(b["tax"]),
      closure: normaliseClosure(b["closure"]),
      billing: normaliseBilling(b["billing"]),
    },
  };
}

const EXPIRIES: readonly CardExpiry[] = ["ok", "soon", "expired"];

/** The Manage facts, tolerant: a card that arrives incomplete is no card. */
export function normaliseBilling(raw: unknown): BillingManage | null {
  if (!raw || typeof raw !== "object") return null;
  const b = raw as Record<string, unknown>;
  const c = (b["card"] ?? null) as Record<string, unknown> | null;
  const expiry = EXPIRIES.find((e) => e === c?.["expiry"]);
  const card =
    c &&
    typeof c["brand"] === "string" &&
    typeof c["last4"] === "string" &&
    typeof c["exp_month"] === "number" &&
    typeof c["exp_year"] === "number" &&
    expiry
      ? {
          brand: c["brand"],
          last4: c["last4"],
          exp_month: c["exp_month"],
          exp_year: c["exp_year"],
          expiry,
        }
      : null;
  const n = (b["notice"] ?? null) as Record<string, unknown> | null;
  const line = textOrNull(n?.["line"]);
  const kind = n?.["kind"] === "action_required" || n?.["kind"] === "failed" ? n["kind"] : null;
  return {
    customer: b["customer"] === true,
    card,
    country: textOrNull(b["country"]),
    latest_invoice_at: textOrNull(b["latest_invoice_at"]),
    notice: line && kind ? { kind, line, invoice_id: textOrNull(n?.["invoice_id"]) } : null,
  };
}

/** Which page of the processor's billing portal: the card, or the billing details. */
export type BillingPortalFlow = "payment_method" | "details";

/**
 * THE PROCESSOR'S BILLING PORTAL, ON ONE PAGE (wave 87). Answers the address to
 * send the browser to; the portal comes back to `/you/plan?portal=1`. Not a
 * cancel path: the portal the server opens cannot cancel or change a plan.
 * `BILLING_PORTAL_UNAVAILABLE` is the server refusing to open one without its
 * own locked configuration.
 */
export async function openBillingPortal(
  flow: BillingPortalFlow,
): Promise<{ ok: true; url: string } | { ok: false; code: string }> {
  const r = await call(() => post("/api/v1/plan/billing-portal", { flow }));
  if (!r) return { ok: false, code: "UNKNOWN" };
  const b = r.body as Record<string, unknown> & RawError;
  if (!r.res.ok) return { ok: false, code: b.error?.code ?? "UNKNOWN" };
  const url = textOrNull(b["url"]);
  return url ? { ok: true, url } : { ok: false, code: "UNKNOWN" };
}

/** Back from the portal: the server reads the customer fresh and answers the Manage facts. */
export async function billingPortalReturned(): Promise<
  PlanResult<{ billing: BillingManage | null }>
> {
  const r = await call(() => post("/api/v1/plan/billing-portal/return", {}));
  if (!r) return PLAN_OFFLINE;
  if (!r.res.ok) return planFailure(r.res.status, r.body as RawError);
  return { ok: true, billing: normaliseBilling((r.body as Record<string, unknown>)["billing"]) };
}

const INVOICE_STATUSES: readonly InvoiceStatus[] = [
  "paid",
  "due",
  "failed",
  "refunded",
  "partly_refunded",
];

/** One page of the invoices and receipts, newest first. */
export async function fetchInvoices(cursor: string | null): Promise<PlanResult<InvoicesPage>> {
  const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : "";
  const r = await call(() => get(`/api/v1/plan/invoices${query}`));
  if (!r) return PLAN_OFFLINE;
  if (!r.res.ok) return planFailure(r.res.status, r.body as RawError);
  const b = r.body as Record<string, unknown>;
  const invoices: InvoiceRow[] = [];
  for (const item of Array.isArray(b["invoices"]) ? b["invoices"] : []) {
    const i = (item ?? {}) as Record<string, unknown>;
    const id = textOrNull(i["id"]);
    const status = INVOICE_STATUSES.find((s) => s === i["status"]);
    if (!id || !status) continue;
    invoices.push({
      id,
      date: textOrNull(i["date"]) ?? "",
      description: textOrNull(i["description"]) ?? "",
      total_cents: num(i["total_cents"]),
      total_display: textOrNull(i["total_display"]) ?? "",
      status,
    });
  }
  return { ok: true, invoices, next_cursor: textOrNull(b["next_cursor"]) };
}

/**
 * An invoice's hosted page or PDF, THROUGH THE SERVER: it checks the invoice is
 * the viewer's and reads the processor's link fresh, then redirects. Opened in
 * a new tab.
 */
export function invoiceLink(id: string, page: "page" | "pdf"): string {
  return `${API_URL}/api/v1/plan/invoices/${encodeURIComponent(id)}/${page}`;
}

/** What the prices include; a server that sent nothing reads as "nothing to say". */
function normaliseTax(raw: unknown): TaxView {
  const t = (raw ?? {}) as Record<string, unknown>;
  const label = t["label"] === "VAT" || t["label"] === "GST" ? t["label"] : null;
  return {
    inclusive: t["inclusive"] === true,
    label,
    suffix: textOrNull(t["suffix"]),
    line: textOrNull(t["line"]),
  };
}

/** A closure waiting on payment, or null. Its sentence is the server's. */
function normaliseClosure(raw: unknown): ClosureView | null {
  if (!raw || typeof raw !== "object") return null;
  const c = raw as Record<string, unknown>;
  const line = textOrNull(c["line"]);
  const state = c["state"];
  if (!line || (state !== "billing" && state !== "waiting_payment" && state !== "ready")) {
    return null;
  }
  return { state, line, bills: normaliseBills(c["bills"]) };
}

/**
 * Tolerant and FAIL-CLOSED on the sentences: a pay-as-you-go block that
 * arrives without its consent text is one nobody could agree to, so it is no
 * block at all rather than a control the server would refuse.
 */
export function normalisePayg(raw: unknown): PaygView | null {
  if (!raw || typeof raw !== "object") return null;
  const p = raw as Record<string, unknown>;
  const consent = textOrNull(p["consent_text"]);
  const drawdown = textOrNull(p["drawdown"]);
  const rate = textOrNull(p["rate_line"]);
  if (!consent || !drawdown || !rate) return null;
  const bills = normaliseBills(p["bills"]);
  return {
    on: p["on"] === true,
    max_cents: typeof p["max_cents"] === "number" ? p["max_cents"] : null,
    max_display: textOrNull(p["max_display"]),
    price_display: typeof p["price_display"] === "string" ? p["price_display"] : "",
    amounts_suffix: textOrNull(p["amounts_suffix"]),
    min_max_cents: num(p["min_max_cents"]),
    max_max_cents: num(p["max_max_cents"]),
    default_max_cents: num(p["default_max_cents"]),
    rate_line: rate,
    accrued_display: typeof p["accrued_display"] === "string" ? p["accrued_display"] : "$0",
    accrued_cents: num(p["accrued_cents"]),
    past_allowance: p["past_allowance"] === true,
    at_maximum: p["at_maximum"] === true,
    available: p["available"] !== false,
    unavailable_reason: textOrNull(p["unavailable_reason"]),
    consent_text: consent,
    off_text: textOrNull(p["off_text"]),
    drawdown,
    bills,
    notices: Array.isArray(p["notices"])
      ? p["notices"].filter((n): n is string => typeof n === "string" && n.length > 0)
      : [],
  };
}

/** Pay-as-you-go bills, tolerant: an entry that is not one is dropped. */
function normaliseBills(raw: unknown): PaygBill[] {
  const bills: PaygBill[] = [];
  if (Array.isArray(raw)) {
    for (const entry of raw) {
      const b = (entry ?? {}) as Record<string, unknown>;
      const id = textOrNull(b["id"]);
      const kind = b["kind"];
      const status = b["status"];
      if (!id) continue;
      if (
        kind !== "early" &&
        kind !== "remainder" &&
        kind !== "quarterly" &&
        kind !== "cancellation"
      )
        continue;
      if (status !== "pending" && status !== "paid" && status !== "failed") continue;
      bills.push({
        id,
        kind,
        amount_display: typeof b["amount_display"] === "string" ? b["amount_display"] : "",
        status,
        on_renewal: b["on_renewal"] === true,
        created_at: typeof b["created_at"] === "string" ? b["created_at"] : "",
        window_start: typeof b["window_start"] === "string" ? b["window_start"] : "",
        window_end: typeof b["window_end"] === "string" ? b["window_end"] : "",
        action_url: textOrNull(b["action_url"]),
        pay_url: textOrNull(b["pay_url"]),
      });
    }
  }
  return bills;
}

export type PaygAction = "on" | "change" | "off";

/**
 * Why pay as you go was not changed. `PAYG_UNAVAILABLE` carries the server's
 * own sentence (something is owed), which the screen renders as it arrived.
 */
export type PaygFailure =
  | { ok: false; code: PlanFailureCode | "NOT_OFFERED" }
  | { ok: false; code: "PAYG_UNAVAILABLE"; reason: string };

function paygFailure(status: number, body: RawError): PaygFailure {
  const error = (body as { error?: { code?: string; reason?: string } }).error;
  if (error?.code === "NOT_OFFERED") return { ok: false, code: "NOT_OFFERED" };
  if (error?.code === "PAYG_UNAVAILABLE") {
    return { ok: false, code: "PAYG_UNAVAILABLE", reason: error.reason ?? "" };
  }
  return planFailure(status, body);
}

/**
 * THE SENTENCE A MAXIMUM WOULD BE AGREED UNDER (ruling R-0923-3 (2)) — asked
 * of the server, because the maximum is inside the words and only the server
 * writes them. A read: nothing is recorded until the confirm.
 */
export async function fetchPaygConsent(
  action: PaygAction,
  maxCents: number | null,
): Promise<{ ok: true; text: string; drawdown: string | null } | PaygFailure> {
  const params = new URLSearchParams({ action });
  if (maxCents !== null) params.set("max_cents", String(maxCents));
  const r = await call(() => get(`/api/v1/plan/payg/consent?${params.toString()}`));
  if (!r) return PLAN_OFFLINE;
  if (!r.res.ok) return paygFailure(r.res.status, r.body as RawError);
  const b = r.body as Record<string, unknown>;
  const text = textOrNull(b["text"]);
  return text ? { ok: true, text, drawdown: textOrNull(b["drawdown"]) } : PLAN_OFFLINE;
}

/**
 * TURN IT ON, CHANGE THE MAXIMUM, OR TURN IT OFF — a consent each time. The
 * sentences go back as their hashes, exactly as they were rendered; the
 * server re-derives its own and refuses on a difference.
 */
export async function setPayg(input: {
  action: PaygAction;
  maxCents: number | null;
  text: string;
  drawdown: string | null;
}): Promise<{ ok: true; payg: PaygView | null } | PaygFailure> {
  const r = await call(async () =>
    post("/api/v1/plan/payg", {
      action: input.action,
      ...(input.maxCents !== null ? { max_cents: input.maxCents } : {}),
      consent_text_hash: await hashConsent(input.text),
      ...(input.drawdown ? { drawdown_hash: await hashConsent(input.drawdown) } : {}),
    }),
  );
  if (!r) return PLAN_OFFLINE;
  if (!r.res.ok) return paygFailure(r.res.status, r.body as RawError);
  return { ok: true, payg: normalisePayg((r.body as Record<string, unknown>)["payg"]) };
}

/**
 * Tolerant, like every parser here: a server that sent none sends an empty
 * list, and an entry missing its words is not a notice the screen can draw.
 */
function normaliseNotices(raw: unknown): PlanNotice[] {
  if (!Array.isArray(raw)) return [];
  const out: PlanNotice[] = [];
  for (const entry of raw) {
    const n = (entry ?? {}) as Record<string, unknown>;
    const id = textOrNull(n["id"]);
    const body = textOrNull(n["body"]);
    const until = textOrNull(n["until"]);
    if (id && body && until) out.push({ id, body, until });
  }
  return out;
}

/**
 * The SHA-256 of the disclosure exactly as it was shown, hex, prefixed the way
 * the server prefixes it. No trimming, no normalising, no case folding: every
 * one of those would be a way for two different sentences to agree.
 */
export async function hashConsent(disclosure: string): Promise<string> {
  const bytes = new TextEncoder().encode(disclosure);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  const hex = Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return `sha256:${hex}`;
}

export type ConsentedChoice = {
  tier: PlanTier;
  interval: PlanInterval;
  first_charge_on: string;
  disclosure: string;
  /** Where the checkout brings the browser back to. The wizard passes "/" so
   * the run resumes at the step it owes; omitted, the plans room. */
  return_to?: string;
  /**
   * The withdrawal-right control's words, exactly as they were rendered, when
   * the server said one applied (ToU 2.1 §8.6). Hashed on the way out, like
   * every other consent here; omitted where the server sent none, and a UK/EEA
   * checkout that omits it is refused with CONSENT_MISMATCH.
   */
  withdrawal_waiver?: string | null;
  /** Which screen this is, so a refusal in the server's log can be placed. */
  surface?: PlanCheckoutSurface;
};

/**
 * Start a subscription. Hands back an OPENING for the modal (ruling R-0825-2)
 * — never a place to send the browser. See `lib/checkout.ts` for why the
 * redirect is gone and what replaced it.
 */
export async function startPlanCheckout(
  choice: ConsentedChoice & {
    /** The drawdown disclosure exactly as rendered beside the subscription's; hashed on the way out. */
    drawdown: string;
  },
): Promise<PlanResult<{ opening: CheckoutOpening }>> {
  const r = await call(async () =>
    post("/api/v1/plan/subscribe", {
      tier: choice.tier,
      interval: choice.interval,
      first_charge_on: choice.first_charge_on,
      consent_text_hash: await hashConsent(choice.disclosure),
      drawdown_hash: await hashConsent(choice.drawdown),
      ...(choice.return_to ? { return_to: choice.return_to } : {}),
      ...(choice.withdrawal_waiver
        ? { withdrawal_waiver_hash: await hashConsent(choice.withdrawal_waiver) }
        : {}),
      ...(choice.surface ? { surface: choice.surface } : {}),
    }),
  );
  if (!r) return PLAN_OFFLINE;
  if (!r.res.ok) return planFailure(r.res.status, r.body as RawError);
  const opening = readOpening(r.body);
  return opening ? { ok: true, opening } : PLAN_OFFLINE;
}

/**
 * Which of the three a change turned out to be (ruling R-0908-1). `kept` is the
 * undo: a scheduled change was withdrawn and the plan stayed where it was.
 */
export type PlanChangeDirection = "upgrade" | "downgrade" | "kept";

/** Which of a trial's two answers (ruling R-0909-1). Null off a trial. */
export type PlanChangeWhen = "now" | "at_trial_end";

/**
 * ONE THING THE OWNER CAN CONFIRM, AND EVERY FIGURE ON IT IS THE SERVER'S
 * (ruling R-0909-1).
 *
 * The dialog composes SENTENCES from these fields — the rung's name, the date,
 * the direction — but it never composes a PRICE. `charge_now_display` and
 * `next_charge_display` arrive as text and are rendered as text, because the
 * amount stated beside a confirm button is the amount somebody consents to.
 */
export type PlanChangeOption = {
  when: PlanChangeWhen | null;
  effective_at: string;
  charge_now_display: string | null;
  next_charge_display: string;
  next_charge_at: string | null;
  first_charge_on: string;
  disclosure: string;
};

export type PlanChangePreview = {
  tier: PlanTier;
  interval: PlanInterval;
  from_tier: PlanTier;
  from_interval: PlanInterval | null;
  direction: PlanChangeDirection;
  trialing: boolean;
  trial_end: string | null;
  /** One, or a trial's two — in offered order, with nothing pre-selected. */
  options: PlanChangeOption[];
};

function normaliseOption(raw: unknown): PlanChangeOption | null {
  const o = (raw ?? {}) as Record<string, unknown>;
  const disclosure = typeof o["disclosure"] === "string" ? o["disclosure"] : "";
  const firstChargeOn = textOrNull(o["first_charge_on"]);
  const effectiveAt = textOrNull(o["effective_at"]);
  // An option with no sentence behind it is one nobody could consent to, and
  // one with no date is one nobody could be told about. Dropped, not patched.
  if (!disclosure || !firstChargeOn || !effectiveAt) return null;
  const when = o["when"];
  return {
    when: when === "now" || when === "at_trial_end" ? when : null,
    effective_at: effectiveAt,
    charge_now_display: textOrNull(o["charge_now_display"]),
    next_charge_display:
      typeof o["next_charge_display"] === "string" ? o["next_charge_display"] : "",
    next_charge_at: textOrNull(o["next_charge_at"]),
    first_charge_on: firstChargeOn,
    disclosure,
  };
}

/**
 * WHAT THIS CHANGE WOULD DO, READ BEFORE ANYTHING IS SENT (ruling R-0909-1).
 *
 * A GET, and it has to stay one: choosing a rung now opens a question rather
 * than making a change, so the read behind that question must not itself be a
 * change. Nothing here posts.
 */
export async function fetchPlanChangePreview(choice: {
  tier: PlanTier;
  interval: PlanInterval;
}): Promise<PlanResult<{ preview: PlanChangePreview }>> {
  const params = new URLSearchParams({ tier: choice.tier, interval: choice.interval });
  const r = await call(() => get(`/api/v1/plan/change/preview?${params.toString()}`));
  if (!r) return PLAN_OFFLINE;
  if (!r.res.ok) return planFailure(r.res.status, r.body as RawError);
  const b = r.body as Record<string, unknown>;
  const tier = tierOf(b["tier"], null);
  const interval = intervalOf(b["interval"]);
  const fromTier = tierOf(b["from_tier"], null);
  if (!tier || tier === "free" || !interval || !fromTier) return PLAN_OFFLINE;
  const options: PlanChangeOption[] = [];
  if (Array.isArray(b["options"])) {
    for (const item of b["options"]) {
      const option = normaliseOption(item);
      if (option) options.push(option);
    }
  }
  if (options.length === 0) return PLAN_OFFLINE;
  const direction = b["direction"];
  return {
    ok: true,
    preview: {
      tier,
      interval,
      from_tier: fromTier,
      from_interval: intervalOf(b["from_interval"]),
      direction:
        direction === "downgrade" ? "downgrade" : direction === "kept" ? "kept" : "upgrade",
      trialing: b["trialing"] === true,
      trial_end: textOrNull(b["trial_end"]),
      options,
    },
  };
}

/**
 * Up the ladder now, down it at the boundary, or back to where you already are.
 * A fresh consent for the first two, which move the recurring amount; the third
 * withdraws a request and moves nothing, so it takes none (ruling R-0908-1).
 */
export async function changePlan(
  choice: ConsentedChoice & { when?: PlanChangeWhen | null },
): Promise<PlanResult<{ effective_at: string; direction: PlanChangeDirection }>> {
  const r = await call(async () =>
    post("/api/v1/plan/change", {
      tier: choice.tier,
      interval: choice.interval,
      first_charge_on: choice.first_charge_on,
      consent_text_hash: await hashConsent(choice.disclosure),
      // Sent only when the owner answered it. An absent `when` is a value on
      // the wire — the server refuses rather than guessing (ruling R-0909-1).
      ...(choice.when ? { when: choice.when } : {}),
      ...(choice.return_to ? { return_to: choice.return_to } : {}),
    }),
  );
  if (!r) return PLAN_OFFLINE;
  if (!r.res.ok) return planFailure(r.res.status, r.body as RawError);
  const b = r.body as Record<string, unknown>;
  const direction = b["direction"];
  return {
    ok: true,
    effective_at: typeof b["effective_at"] === "string" ? b["effective_at"] : "",
    direction: direction === "downgrade" ? "downgrade" : direction === "kept" ? "kept" : "upgrade",
  };
}

/**
 * ONE CALL, AND IT IS THE WHOLE CANCEL PATH (§9 #4). No portal redirect, no
 * second endpoint, no interstitial in between. Whatever the screen wants to say
 * about staying, it says beside the one button that does this.
 */
export async function cancelPlan(): Promise<PlanResult<{ ends_on: string; tier: string }>> {
  const r = await call(() => post("/api/v1/plan/cancel", {}));
  if (!r) return PLAN_OFFLINE;
  if (!r.res.ok) return planFailure(r.res.status, r.body as RawError);
  const b = r.body as Record<string, unknown>;
  return {
    ok: true,
    ends_on: typeof b["ends_on"] === "string" ? b["ends_on"] : "",
    tier: typeof b["tier"] === "string" ? b["tier"] : "",
  };
}
